import arrayMutators from 'final-form-arrays'
import React from 'react'
import { Form, FormRenderProps } from 'react-final-form'

import Actions from '@cinch/oxd-components/lifestyle-actions/src'
import ProgressBar from '@cinch/oxd-components/progress-bar/src'
import Layout from '@cinch/oxd-layouts/regular/src'

import FieldSet from './components/fieldset.component'
import GetStarted from './components/get-started/get-started.component'

import * as constants from './constants'
import steps from './steps-config'

import * as styles from './lifestyle.module.css'

/**
 * This mutator returns the form value for the first param
 */
const getValue = ([name]: any, state: any, { getIn }: any) => getIn(state.formState.values, name)

export interface LifestyleComponentProps {
  currentPage: number
  i18n: Cinch.WebAppTranslations['lifestyle']
  initialValues: any
  isPending: boolean
  onPageNext: (values: any, currentPage: number) => void
  goToPage: (pageNumber: number) => void
}

const Lifestyle: React.FC<LifestyleComponentProps> = ({
  currentPage,
  i18n,
  onPageNext,
  goToPage,
  isPending,
  initialValues,
}) => {
  const isIntroductionPage = currentPage === 0
  const stepData = steps[currentPage - 1] || {}
  const Component = stepData.component || null
  const Aside = stepData.aside
  const onSubmit = (values: any) => onPageNext(values, currentPage)
  const progressRef = React.createRef<HTMLDivElement>()

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const scrollY =
        progressRef && progressRef.current ? (progressRef.current as HTMLElement).getBoundingClientRect().top : 0
      if (scrollY < 0) {
        if ('scrollBehavior' in document.documentElement.style) {
          window.scrollTo({
            top: window.pageYOffset + scrollY,
            behavior: 'smooth',
          })
        } else {
          window.scrollTo(0, window.pageYOffset + scrollY)
        }
      }
    }
  }, [currentPage])

  React.useEffect(() => {
    if (currentPage !== 1 && !initialValues.budget) {
      goToPage(1)
    }
  }, [currentPage, initialValues.budget])

  const RenderForm = (fieldRenderProps: FormRenderProps) => (
    <form name="lifestyle" onSubmit={fieldRenderProps.handleSubmit} className={styles.form}>
      <div ref={progressRef} className={styles.pagination}>
        <ProgressBar theme="light" progress={(currentPage - 1) / constants.TOTAL_PAGES} />
      </div>
      <FieldSet
        isFirstPage={currentPage === 1}
        remainingSteps={constants.TOTAL_PAGES - currentPage}
        title={i18n[stepData.i18nFragment].title}
        intro={i18n[stepData.i18nFragment].intro}
        aside={!!stepData.aside && <Aside i18n={i18n[stepData.i18nFragment]} {...fieldRenderProps} />}
      >
        <Component i18n={i18n[stepData.i18nFragment]} {...fieldRenderProps} />
      </FieldSet>
      <div className={styles.cardDropShadow} />
      <Actions
        className={styles.actions}
        i18n={i18n.actions}
        resultLink={constants.RECOMMENDED_CARS_URL}
        goToPage={goToPage}
        currentPage={currentPage}
        isPending={isPending}
        isNextDisabled={fieldRenderProps.invalid}
        allowSkip={currentPage > 1}
      />
    </form>
  )

  return (
    <Layout
      isFullHeightHero={true}
      hero={
        isIntroductionPage ? (
          <GetStarted i18n={i18n.intro} onClick={() => goToPage(1)} />
        ) : (
          !!currentPage && (
            <Form
              validate={(values) => (!(values as any).budget ? { budget: i18n.required } : {})}
              onSubmit={onSubmit}
              render={RenderForm}
              initialValues={initialValues}
              mutators={{ ...arrayMutators, getValue }}
            />
          )
        )
      }
    />
  )
}

export default Lifestyle
